import { create } from 'zustand';
import { supabase } from './supabase';

interface WaitlistStore {
  count: number;
  loading: boolean;
  error: string | null;
  isAnimating: boolean;
  initialized: boolean;
  setCount: (count: number) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  setAnimating: (isAnimating: boolean) => void;
  setInitialized: (initialized: boolean) => void;
  fetchCount: () => Promise<void>;
  subscribeToChanges: () => void;
  unsubscribeFromChanges: () => void;
}

export const useWaitlistStore = create<WaitlistStore>((set, get) => {
  // Store the channel reference for cleanup
  let channel: ReturnType<typeof supabase.channel> | null = null;

  return {
    count: 0,
    loading: true,
    error: null,
    isAnimating: false,
    initialized: false,
    setCount: (count) => set({ count }),
    setLoading: (loading) => set({ loading }),
    setError: (error) => set({ error }),
    setAnimating: (isAnimating) => set({ isAnimating }),
    setInitialized: (initialized) => set({ initialized }),
    fetchCount: async () => {
      console.log('Fetching waitlist count...');
      if (get().loading && get().initialized) {
        console.log('Skipping fetch - already loading or initialized');
        return;
      }

      try {
        set({ loading: true, error: null });
        console.log('Making Supabase request...');

        const { count, error } = await supabase
          .from('waitlist')
          .select('*', { count: 'exact', head: true });

        if (error) {
          console.error('Supabase error:', error);
          throw error;
        }

        console.log('Received count:', count);
        if (typeof count === 'number') {
          const previousCount = get().count;
          const isNewCount = count !== previousCount;
          
          set({ 
            count,
            loading: false,
            initialized: true,
            isAnimating: isNewCount
          });

          if (isNewCount) {
            console.log('Count updated:', previousCount, '->', count);
            // Reset animation after 1s
            setTimeout(() => {
              set({ isAnimating: false });
            }, 1000);
          }
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to fetch count';
        console.error('Error fetching count:', errorMessage);
        set({ 
          error: errorMessage,
          loading: false
        });
      }
    },
    subscribeToChanges: () => {
      console.log('Setting up realtime subscription...');
      
      // Clean up existing subscription if any
      if (channel) {
        console.log('Cleaning up existing subscription');
        channel.unsubscribe();
      }

      // Create new subscription
      channel = supabase
        .channel('waitlist_changes')
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'waitlist'
          },
          (payload) => {
            console.log('Realtime event received:', payload);
            get().fetchCount();
          }
        )
        .subscribe((status) => {
          console.log('Subscription status:', status);
        });
    },
    unsubscribeFromChanges: () => {
      if (channel) {
        console.log('Unsubscribing from realtime changes');
        channel.unsubscribe();
        channel = null;
      }
    }
  };
});

// Initialize subscription
useWaitlistStore.getState().subscribeToChanges();